import { Box, BoxProps, IconButton, ListItem, Stack, styled } from '@mui/material';
import { forwardRef } from 'react';
import { GLOBAL_TABS_HEIGHT } from 'src/components/GlobalTabs/styled';

export const NavigationSidePanel = styled(Box, { name: 'NavigationSidePanel' })(
  ({ theme }) => ({
    position: 'static',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    width: '100%',
    height: '100%',
    display: 'flex',
    flexShrink: 0,

    [theme.breakpoints.down('sm')]: {
      width: '100%',
    },
  }),
);

export const MobileHeaderNavContainer = styled(Stack, {
  name: 'MobileHeaderNavContainer',
})(({ theme }) => ({
  width: '100%',
  height: '3rem',
  flexGrow: 1,
  flexDirection: 'row',
  borderBottom: '1px solid',
  borderColor: theme.palette.custom.OV.sys.outlineVariant,
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.custom.OV.sys.background,
}));

export const ListHeader = styled('header', {
  name: 'ListHeader',
})(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyItems: 'start',
  gap: '0.25rem',
  alignItems: 'center',
  height: GLOBAL_TABS_HEIGHT,
  flexShrink: 0,
  boxSizing: 'border-box',
  padding: '0 0.5rem',
  position: 'relative',
}));

export const LogoContainer = styled(Box, { name: 'LogoContainer' })(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: '0.3rem',
  marginRight: '0.3rem',
  lineHeight: 1,
  '& img': {
    width: '1.5rem',
    height: '1.5rem',
  },
}));

export const PartnerHeaderContainer = styled(Box, {
  name: 'PartnerHeaderContainer',
})(({ theme }) => ({
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  flexDirection: 'row',
  overflow: 'hidden',
  minWidth: 0,
  borderRadius: theme.shape.borderRadius,
  paddingRight: '0.5rem',
  '&:hover': {
    backgroundColor: `color-mix(in srgb, ${theme.palette.custom.OV.sys.onSurface} 10%, transparent)`,
  },
}));

export const ListHeaderIcon = styled(IconButton, {
  name: 'ListHeaderIcon',
})(({ theme }) => ({
  width: '1.5rem',
  height: '1.5rem',
  borderRadius: theme.shape.borderRadius / 2,
  padding: '0.125rem',
  flexShrink: 0,
  minWidth: 0,

  pointerEvents: theme.utils.important('auto'),
  color: theme.palette.custom.OV.sys.onSurfaceVariant,

  '&:hover': {
    backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHighest,
  },

  '&[disabled]': {
    cursor: theme.utils.important('not-allowed'),
  },

  '& svg': {
    width: '100%',
    height: '100%',
  },
}));

export const NavItem = styled(ListItem, {
  name: 'NavItem',
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active: boolean }>(({ theme, active }) => ({
  height: 'auto',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  transition: 'color 0.1s ease-in-out',
  '&:hover': {
    color: theme.palette.custom.OV.sys.inverseSurface,
  },
  padding: 0,
  '& .MuiListItemButton-root': {
    height: '1.875rem',
    backgroundColor: active ? theme.palette.custom.OV.sys.secondaryContainer : 'transparent',
    borderRadius: '0.375rem',
    padding: '0 0.5rem',
    '&:hover': {
      backgroundColor: active
        ? theme.palette.custom.OV.sys.secondaryContainer
        : theme.palette.custom.OV.sys.surfaceContainerHighest,
    },
    '@media (hover: none)': {
      '&:hover': {
        backgroundColor: active
          ? theme.palette.custom.OV.sys.secondaryContainer
          : 'transparent',
      },
    },
  },
}));

const NavigationSectionAdornmentWrapperRoot = styled(Box)(() => ({
  visibility: 'hidden',
  cursor: 'pointer',
}));

const navigationSectionAdornmentClassName = 'navigation-section-adornment-wrapper';

export const NavigationSectionAdornmentWrapper = forwardRef<HTMLDivElement, BoxProps>(
  ({ className, ...props }, ref) => (
    <NavigationSectionAdornmentWrapperRoot
      ref={ref}
      className={[navigationSectionAdornmentClassName, className].filter(Boolean).join(' ')}
      {...props}
    />
  ),
);

NavigationSectionAdornmentWrapper.displayName = 'NavigationSectionAdornmentWrapper';

export const NavbarHoverTrap = styled(Box, { name: 'NavbarHoverTrap' })(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  bottom: 0,
  width: '1.375rem',
  zIndex: 40,
  cursor: 'pointer',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '0.25rem',
    transform: 'translateY(-50%)',
    width: '3px',
    height: '2.25rem',
    borderRadius: '0 3px 3px 0',
    backgroundColor: theme.palette.custom.OV.sys.outlineVariant,
    opacity: 0.6,
    transition: theme.transitions.create(['opacity', 'background-color', 'height'], {
      duration: theme.transitions.duration.shorter,
    }),
  },

  '&:hover::before': {
    opacity: 1,
    backgroundColor: theme.palette.custom.OV.sys.secondary,
    height: '4rem',
  },
}));

export const NavbarResizeHandle = styled('hr', {
  name: 'NavbarResizeHandle',
  shouldForwardProp: (prop) => prop !== 'isDragging',
})<{ isDragging?: boolean }>(({ theme, isDragging }) => ({
  position: 'absolute',
  top: 0,
  bottom: 0,
  right: 0,
  border: 0,
  margin: 0,
  width: '0.5rem',
  cursor: 'col-resize',
  backgroundColor: 'transparent',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '2px',
    height: '2.25rem',
    borderRadius: '2px',
    backgroundColor: theme.palette.custom.OV.sys.outline,
    opacity: 0,
    transition: theme.transitions.create(['opacity', 'background-color', 'height'], {
      duration: theme.transitions.duration.shorter,
    }),
  },

  '&:hover::before': {
    opacity: 0.9,
    height: '3rem',
  },

  ...(isDragging && {
    '&::before, &:hover::before': {
      opacity: 1,
      height: '4.5rem',
      backgroundColor: theme.palette.custom.OV.sys.secondary,
    },
  }),
}));

export const NavigationSectionContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  userSelect: 'none',
  cursor: 'pointer',
  // fixed height so the hover background matches headers with and without an
  // options button; spacing above the group lives in the margin, not padding
  height: '1.75rem',
  padding: '0 0.5rem',
  margin: '0.375rem 0 0',
  borderRadius: theme.shape.borderRadius,
  '&:hover': {
    backgroundColor: `color-mix(in srgb, ${theme.palette.custom.OV.sys.onSurface} 10%, transparent)`,

    [`& .${navigationSectionAdornmentClassName}`]: {
      visibility: 'visible',
    },
  },
}));
